import { useState, useMemo, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router';
import KpiCard from '@/components/KpiCard';
import Gauge from '@/components/Gauge';
import SavedAssessmentsDosing from '@/components/SavedAssessmentsDosing';
import { staggerContainer } from '@/lib/animations';
import { useCalculator } from '@/hooks/useCalculator';
import { useFlowingSparkline } from '@/hooks/useAnimatedKpi';
import { useSession } from '@/context/SessionContext';
import { useAuth } from '@/hooks/useAuth';
import { getStandard } from '@/types/standards';
import { AVAILABLE_CHEMICALS } from '@/types/calculator';
import { apiListReports, apiListSamples, apiListCustomers } from '@/lib/phpApi';
import {
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  AreaChart, Area, Legend,
} from 'recharts';
import {
  Plus, Play, FileText, AlertTriangle, Activity,
  Droplets, ArrowRight, Building2, Beaker, FlaskConical,
  Database, CheckCircle2, XCircle, TrendingUp, Users,
  ShieldCheck, Zap, Info, ChevronRight, Lock, Unlock,
  Globe, Server, HardDrive, BookOpen, Target, Layers, Filter,
  BarChart3, Thermometer, Wind, Droplet, Factory, Settings,
  LayoutDashboard,
} from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import FeatureCard from '@/components/FeatureCard';

// ─── Chemical color helper (also defined in DosingPage) ───
const CHEM_COLORS: Record<string, string> = {
  fecl3: '#f59e0b', alum: '#3b82f6', pac: '#06b6d4', feso4: '#22c55e',
  lime: 'var(--rb-text-2)', caustic: '#ef4444', polymer: '#a855f7', chlorine: '#f97316',
};
function chemColor(id: string) { return CHEM_COLORS[id] || 'var(--rb-text-3)'; }

interface CustomerSample {
  customerId: string; customerName: string; sampleName: string;
  date: string; params?: Record<string, number>;
}
function loadCustomerSamples(): CustomerSample[] {
  try { return JSON.parse(localStorage.getItem('rb_customer_samples') || '[]'); } catch { return []; }
}
function loadCustomers() {
  try { return JSON.parse(localStorage.getItem('rb_customers') || '[]'); } catch { return []; }
}
function loadSavedReports(): Array<{ id: string; title: string; date: string; type: string }> {
  try { return JSON.parse(localStorage.getItem('rb_saved_reports') || '[]'); } catch { return []; }
}
function loadSampleReports(): Array<{ id: string; title: string; date: string; type: string }> {
  try { return JSON.parse(localStorage.getItem('rb_sample_reports') || '[]'); } catch { return []; }
}
function loadAssessmentReports(): Array<{ id: string; title: string; date: string; type: string }> {
  try { return JSON.parse(localStorage.getItem('rb_reports') || '[]'); } catch { return []; }
}
function getAllActivities(): Array<{ action: string; timestamp: string; username: string; details?: string }> {
  try { return JSON.parse(localStorage.getItem('rb_user_activity') || '[]'); } catch { return []; }
}
function loadStoredDosing() {
  try { return JSON.parse(localStorage.getItem('rb_dosing_config_v2') || '[]'); } catch { return []; }
}

// Compute compliance % for a sample against discharge standard limits
function computeSampleCompliance(s: CustomerSample, limits: { bod5: number; cod: number; tss: number; oilGrease: number; ammoniaN: number; totalPhosphorus: number }): number {
  if (!s.params) return -1; // no params — unknown
  const checks = [
    { val: s.params.bod5 ?? s.params.bod, limit: limits.bod5 },
    { val: s.params.cod,              limit: limits.cod },
    { val: s.params.tss,              limit: limits.tss },
    { val: s.params.oilGrease,        limit: limits.oilGrease },
    { val: s.params.ammoniaN,         limit: limits.ammoniaN },
    { val: s.params.totalPhosphorus,  limit: limits.totalPhosphorus },
  ].filter(c => c.val !== undefined && c.val !== null && c.limit > 0);
  if (!checks.length) return -1;
  const pass = checks.filter(c => c.val! <= c.limit!).length;
  return Math.round((pass / checks.length) * 100);
}

function daysSince(dateStr: string): number {
  return Math.floor((Date.now() - new Date(dateStr).getTime()) / 86_400_000);
}

// ─── Educational content for dashboard ───
const PARAM_GUIDES = [
  { key: 'bod5', name: 'BOD₅', full: '5-Day Biochemical Oxygen Demand', icon: Droplets, color: '#22d3ee',
    desc: 'Measures oxygen consumed by bacteria decomposing organic matter. High BOD means high organic pollution — biological treatment (activated sludge, MBBR) is needed.',
    ranges: [{ label: 'Low', max: 20, color: 'text-emerald-400' }, { label: 'Moderate', max: 300, color: 'text-amber-400' }, { label: 'High', max: 2000, color: 'text-red-400' }] },
  { key: 'cod', name: 'COD', full: 'Chemical Oxygen Demand', icon: FlaskConical, color: '#a78bfa',
    desc: 'Total oxygen required to chemically oxidise all organic material. COD is always higher than BOD. A COD/BOD ratio >2.5 suggests non-biodegradable compounds — may need chemical oxidation.',
    ranges: [{ label: 'Low', max: 125, color: 'text-emerald-400' }, { label: 'Moderate', max: 1000, color: 'text-amber-400' }, { label: 'High', max: 10000, color: 'text-red-400' }] },
  { key: 'tss', name: 'TSS', full: 'Total Suspended Solids', icon: Layers, color: '#f59e0b',
    desc: 'Particulate matter that can be trapped by filtration. High TSS requires physical separation — screening, sedimentation (Lamella), DAF, or sand filtration before biological treatment.',
    ranges: [{ label: 'Low', max: 30, color: 'text-emerald-400' }, { label: 'Moderate', max: 350, color: 'text-amber-400' }, { label: 'High', max: 5000, color: 'text-red-400' }] },
  { key: 'ph', name: 'pH', full: 'Acidity / Alkalinity', icon: Beaker, color: '#34d399',
    desc: 'pH must be 6.5–8.5 for biological treatment. Outside this range, bacteria die. Acidic wastewater needs lime/caustic dosing; alkaline waste needs acid or CO₂ addition.',
    ranges: [{ label: 'Optimal', min: 6.5, max: 8.5, color: 'text-emerald-400' }, { label: 'Marginal', max: 9, color: 'text-amber-400' }, { label: 'Extreme', color: 'text-red-400' }] },
  { key: 'oilGrease', name: 'O&G', full: 'Oil & Grease', icon: Wind, color: 'var(--rb-text-2)',
    desc: 'Oil and grease coat surfaces and clog biological systems. Free oil is removed by gravity separation (API separator); emulsified oil needs DAF with coagulant dosing.',
    ranges: [{ label: 'Low', max: 15, color: 'text-emerald-400' }, { label: 'Moderate', max: 50, color: 'text-amber-400' }, { label: 'High', max: 500, color: 'text-red-400' }] },
  { key: 'conductivity', name: 'Conductivity', full: 'Electrical Conductivity', icon: Zap, color: '#fb923c',
    desc: 'Indicates dissolved salt content. >5000 µS/cm suggests saline wastewater which limits biological treatment options. May require evaporation, RO, or ion exchange.',
    ranges: [{ label: 'Fresh', max: 1500, color: 'text-emerald-400' }, { label: 'Brackish', max: 5000, color: 'text-amber-400' }, { label: 'Saline', color: 'text-red-400' }] },
];

// ─── Treatment process stages ───
const PROCESS_STAGES = [
  { name: 'Pre-Treatment', icon: Factory, color: 'var(--rb-text-3)', steps: ['Screening', 'Grit removal', 'Flow equalisation'], purpose: 'Remove large solids and protect downstream equipment' },
  { name: 'Primary Treatment', icon: Layers, color: '#3b82f6', steps: ['Sedimentation', 'DAF', 'Oil separation'], purpose: 'Remove settleable and floatable solids' },
  { name: 'Chemical Treatment', icon: FlaskConical, color: '#f59e0b', steps: ['Coagulation', 'Flocculation', 'pH adjustment'], purpose: 'Precipitate dissolved contaminants and condition water' },
  { name: 'Biological Treatment', icon: Droplet, color: '#22c55e', steps: ['Activated sludge', 'MBBR', 'MBR', 'SBR'], purpose: 'Bacteria consume dissolved organics (BOD/COD)' },
  { name: 'Tertiary Treatment', icon: ShieldCheck, color: '#a855f7', steps: ['Sand filtration', 'Carbon adsorption', 'Nutrient removal'], purpose: 'Polish effluent to meet discharge standards' },
  { name: 'Disinfection', icon: Zap, color: '#ef4444', steps: ['Chlorination', 'UV', 'Ozonation'], purpose: 'Kill pathogens before discharge' },
];

export default function DashboardPage() {
  const navigate = useNavigate();
  const calc = useCalculator();
  const { activeStandard, currentCustomer, userProfile } = useSession();
  const [localSamples] = useState<CustomerSample[]>(loadCustomerSamples);
  const [localCustomers] = useState(loadCustomers);
  const [showGuides, setShowGuides] = useState(false);

  const standard = getStandard(activeStandard);
  const { isAdmin } = useAuth();

  // ─── Load samples & customers from MySQL backend when available ───
  const [dbSamples, setDbSamples] = useState<CustomerSample[]>([]);
  const [dbCustomers, setDbCustomers] = useState<any[]>([]);

  useEffect(() => {
    async function loadDbData() {
      try {
        const [samplesRows, customersRows] = await Promise.all([
          apiListSamples().catch(() => []),
          apiListCustomers().catch(() => []),
        ]);
        setDbSamples(Array.isArray(samplesRows) ? samplesRows : (samplesRows as any)?.samples || []);
        setDbCustomers(Array.isArray(customersRows) ? customersRows : (customersRows as any)?.customers || []);
      } catch (err) {
        console.warn('Failed to load dashboard data from backend:', err);
      }
    }
    loadDbData();
  }, []);

  // Use backend data when available, otherwise fall back to localStorage
  const samples = dbSamples.length > 0 ? dbSamples : localSamples;
  const customers = dbCustomers.length > 0 ? dbCustomers : localCustomers;
  const savedReports = loadSavedReports();
  const sampleReports = loadSampleReports();
  const assessmentReports = loadAssessmentReports();

  // ─── Load reports count from MySQL database ONLY ───
  const [dbReportsCount, setDbReportsCount] = useState<number>(0);
  useEffect(() => {
    async function loadDbReports() {
      try {
        const reports = await apiListReports();
        setDbReportsCount(Array.isArray(reports) ? reports.length : 0);
      } catch (err) {
        console.warn('Failed to load reports from backend:', err);
        setDbReportsCount(0);
      }
    }
    loadDbReports();
  }, []);

  const totalReports = dbReportsCount;
  const activities = getAllActivities().slice(0, 10);
  const dosingConfigs = loadStoredDosing();
  const activeDosingCount = dosingConfigs.filter((d: { enabled?: boolean }) => d.enabled).length;

  const recentSamples = [...samples].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).slice(0, 5);
  const hasData = samples.length > 0;

  // Stats
  const stats = useMemo(() => [
    { label: 'Customers', value: customers.length, icon: Users, color: '#22d3ee', bg: 'bg-cyan-500/10', border: 'border-cyan-500/20' },
    { label: 'Samples', value: samples.length, icon: Beaker, color: 'var(--rb-brand)', bg: 'bg-rb-brand/10', border: 'border-rb-brand/20' },
    { label: 'Reports', value: totalReports, icon: FileText, color: '#34d399', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20' },
    { label: 'Dosing Active', value: activeDosingCount, icon: FlaskConical, color: '#a855f7', bg: 'bg-purple-500/10', border: 'border-purple-500/20' },
  ], [customers.length, samples.length, totalReports, activeDosingCount]);

  const quickActions = [
    { label: 'New Sample',      icon: Plus,         onClick: () => navigate('/samples'),         color: '#22d3ee',        desc: 'Log water quality data' },
    { label: 'Run Assessment',  icon: Play,         onClick: () => navigate('/parameters'),      color: '#34d399',        desc: 'Analyse treatability' },
    { label: 'Generate Report', icon: FileText,     onClick: () => navigate('/reports'),         color: '#f59e0b',        desc: 'Create trend reports' },
    { label: 'Manage Dosing',   icon: FlaskConical, onClick: () => navigate('/dosing'),          color: '#a855f7',        desc: 'Adjust chemical dosing' },
    { label: 'Add Effluent',    icon: Beaker,       onClick: () => navigate('/samples'),         color: 'var(--rb-brand)', desc: 'Post-treatment water sample' },
    { label: 'Influent Data',   icon: Droplets,     onClick: () => navigate('/influent-entry'),  color: '#38bdf8',        desc: 'Raw water sample data' },
  ];

// ─── Realistic DAF Operating Parameters ─────────────────────────
// Loaded from localStorage if saved, otherwise use typical defaults
interface DAFOperatingParams {
  coagulantType: 'fecl3' | 'alum' | 'pac' | 'none';
  coagulantDose: number;     // mg/L
  polymerDose: number;      // mg/L
  recycleRate: number;      // % of flow
  saturatorPressure: number; // bar
  hydraulicLoading: number; // m/hr
  retentionTime: number;    // min
  flocRetention: number;    // min
  flocGValue: number;       // s⁻¹
  temperature: number;       // °C
}

function loadDAFParams(): DAFOperatingParams {
  try {
    const saved = localStorage.getItem('rb_daf_operating_params');
    if (saved) return { ...dafDefaults, ...JSON.parse(saved) };
  } catch { /* ignore */ }
  return { ...dafDefaults };
}

const dafDefaults: DAFOperatingParams = {
  coagulantType: 'fecl3',
  coagulantDose: 25,      // FeCl₃ 25 mg/L — typical for food industry
  polymerDose: 2.0,       // Polyelectrolyte 2 mg/L
  recycleRate: 15,        // 15% recycle — standard
  saturatorPressure: 5.0, // 5 bar saturator
  hydraulicLoading: 8.0,  // 8 m³/m²/hr — within DAF optimal range (5-12)
  retentionTime: 15,      // 15 min in DAF cell
  flocRetention: 10,      // 10 min flocculation
  flocGValue: 75,         // 75 s⁻¹ — gentle flocculation
  temperature: 22,        // 22°C influent
};

/* ─── DAF Engineering Model ────────────────────────────────────────
   Physics-based effluent calculator. Removal efficiency depends on:
   • Coagulant dose → charge neutralisation, enmeshment
   • Polymer dose → floc size, bubble attachment
   • Recycle rate + pressure → air bubble concentration (A/S ratio)
   • Hydraulic loading → rise velocity vs overflow rate
   • Temperature → viscosity, bubble size
   • pH → coagulant hydrolysis chemistry

   Realistic removal ranges for well-operated DAF:
   TSS:  85-95%  |  COD: 35-55% (particulate only)
   BOD₅: 30-45%  |  O&G: 85-95%
   ────────────────────────────────────────────────────────────────── */
function calculateDAFEffluent(
  influent: { bod5: number; cod: number; tss: number; oilGrease: number; ph: number; temp: number; ammoniaN: number; flowRate: number; conductivity: number; },
  op: DAFOperatingParams,
): {
  bod5: number; cod: number; tss: number; oilGrease: number; ph: number;
  temperature: number; ammoniaN: number; flowRate: number; conductivity: number;
  removalPct: { bod5: number; cod: number; tss: number; oilGrease: number };
  airToSolids: number; flocQuality: number;
} {
  // ─── Coagulant chemistry ───
  const coagFactor = op.coagulantType === 'none' ? 0 : op.coagulantDose / 50; // normalised to 50 mg/L max
  const coagBoostTSS = Math.min(0.35, coagFactor * 0.35); // up to +35% TSS removal
  const coagBoostCOD = Math.min(0.25, coagFactor * 0.25); // up to +25% COD (particulate)
  const coagBoostBOD = Math.min(0.20, coagFactor * 0.20); // up to +20% BOD (particulate)

  // ─── Polymer effect ───
  const polymerFactor = Math.min(1, op.polymerDose / 4); // normalised to 4 mg/L max
  const polymerBoostTSS = polymerFactor * 0.10;
  const polymerBoostOG  = polymerFactor * 0.15; // polymer critical for emulsified oil

  // ─── Air-to-solids ratio (A/S) ───
  // A/S = (recycle% × pressure × solubility) / (TSS × 10)
  // Henry's law: air solubility ~ 25 mg/L at 5 bar, 20°C
  const airSolubility = 25 * (op.saturatorPressure / 5) * (1 - 0.02 * (op.temperature - 20));
  const airInjected = (op.recycleRate / 100) * airSolubility; // mg/L air in feed
  const airToSolids = airInjected / Math.max(influent.tss * 0.1, 5);
  const airFactor = Math.min(1, airToSolids / 0.05); // optimal A/S ≈ 0.03-0.08
  const airBoostTSS = airFactor * 0.12;
  const airBoostOG  = airFactor * 0.10;

  // ─── Hydraulic loading check ───
  // DAF optimal: 5-12 m/hr. >15 = poor removal, <3 = over-designed
  const loadingFactor = op.hydraulicLoading > 15 ? 0.70 : op.hydraulicLoading < 3 ? 0.85 : 1.0;

  // ─── Temperature/viscosity effect ───
  // Higher temp = lower viscosity = faster bubble rise = better removal
  const tempFactor = 1 + 0.008 * (op.temperature - 15); // +0.8% per °C above 15°C

  // ─── Flocculation quality ───
  // G-value 50-100 s⁻¹ optimal for DAF floc. Too high = breakup, too low = no growth
  const gOptimal = op.flocGValue >= 50 && op.flocGValue <= 100 ? 1.0 : 0.85;
  const retentionOptimal = op.flocRetention >= 8 && op.flocRetention <= 15 ? 1.0 : 0.90;
  const flocQuality = gOptimal * retentionOptimal;

  // ─── Base removal + all boosts ───
  const tssRemoval  = Math.min(0.96, (0.55 + coagBoostTSS + polymerBoostTSS + airBoostTSS) * loadingFactor * tempFactor * flocQuality);
  const codRemoval  = Math.min(0.60, (0.20 + coagBoostCOD) * loadingFactor * tempFactor * flocQuality);
  const bodRemoval  = Math.min(0.50, (0.15 + coagBoostBOD) * loadingFactor * tempFactor * flocQuality);
  const ogRemoval   = Math.min(0.97, (0.65 + polymerBoostOG + airBoostOG) * loadingFactor * tempFactor);

  // ─── pH adjustment from coagulant ───
  // FeCl₃ produces HCl: ~0.01 pH drop per 10 mg/L
  // Alum produces H⁺: ~0.006 pH drop per 10 mg/L
  // PAC is pre-hydrolysed: minimal pH effect
  const phDrop = op.coagulantType === 'fecl3' ? op.coagulantDose * 0.001 :
                 op.coagulantType === 'alum'  ? op.coagulantDose * 0.0006 :
                 op.coagulantType === 'pac'   ? op.coagulantDose * 0.0002 : 0;
  const effluentPH = Math.max(5.5, influent.ph - phDrop);

  return {
    bod5: influent.bod5 * (1 - bodRemoval),
    cod:  influent.cod  * (1 - codRemoval),
    tss:  influent.tss  * (1 - tssRemoval),
    oilGrease: influent.oilGrease * (1 - ogRemoval),
    ph: effluentPH,
    temperature: influent.temp,
    ammoniaN: influent.ammoniaN * 0.97, // minimal ammonia removal in DAF
    flowRate: influent.flowRate,
    conductivity: influent.conductivity,
    removalPct: {
      bod5: Math.round(bodRemoval * 100),
      cod:  Math.round(codRemoval * 100),
      tss:  Math.round(tssRemoval * 100),
      oilGrease: Math.round(ogRemoval * 100),
    },
    airToSolids: Number(airToSolids.toFixed(3)),
    flocQuality: Number(flocQuality.toFixed(2)),
  };
}

// ─── Stable DAF operation fluctuation (±3% not ±8%) ───
function useStableKpi(baseValue: number, label: string, unit: string) {
  const [state, setState] = useState({ value: baseValue, pctChange: 0, isAlert: false, direction: 'stable' as const });
  useEffect(() => {
    if (baseValue <= 0) return;
    let id: ReturnType<typeof setTimeout>;
    const tick = () => {
      const wave = Math.sin(Date.now() / 8000) * 0.6 + Math.sin(Date.now() / 13000) * 0.4;
      const pct = wave * 3; // ±3%
      setState({
        value: baseValue * (1 + pct / 100),
        pctChange: pct,
        isAlert: Math.abs(pct) > 2.8,
        direction: pct > 0.3 ? 'up' : pct < -0.3 ? 'down' : 'stable',
      });
      id = setTimeout(tick, 3000 + Math.random() * 2000);
    };
    tick();
    return () => clearTimeout(id);
  }, [baseValue]);
  return state;
}

  // ─── Load DAF operating parameters & calculate realistic effluent ───
  const dafOp = useMemo(() => loadDAFParams(), []);
  const cp = calc.params;
  const hasCalc = calc.hasCalculated && calc.result;

  const influent = {
    bod5: cp.bod5 || 850,
    cod: cp.cod || 1350,
    tss: cp.tss || 420,
    oilGrease: cp.oilGrease || 85,
    ph: cp.ph || 7.2,
    temp: cp.temperature || 22,
    ammoniaN: cp.ammoniaN || 12,
    flowRate: cp.flowRate || 150,
    conductivity: cp.conductivity || 1760,
  };

  const dafResult = useMemo(() => calculateDAFEffluent(influent, dafOp), [influent, dafOp]);

  // Standard limits for comparison
  const stdLimits = standard.limits;

  // ─── Dynamic alerts computed from live KPI values ───
  const alerts = useMemo(() => {
    const now = new Date();
    const fmt = (d: Date) => d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    const list: Array<{ id: string; parameter: string; message: string; severity: 'critical' | 'warning' | 'info'; time: string }> = [];
    if (dafResult.bod5 > stdLimits.bod5)
      list.push({ id: 'bod', parameter: 'BOD₅', message: `DAF effluent BOD₅ ${dafResult.bod5.toFixed(0)} mg/L exceeds limit of ${stdLimits.bod5} mg/L`, severity: 'critical', time: fmt(now) });
    else if (dafResult.bod5 > stdLimits.bod5 * 0.8)
      list.push({ id: 'bod', parameter: 'BOD₅', message: `BOD₅ ${dafResult.bod5.toFixed(0)} mg/L — approaching limit (${stdLimits.bod5} mg/L)`, severity: 'warning', time: fmt(now) });
    if (dafResult.cod > stdLimits.cod)
      list.push({ id: 'cod', parameter: 'COD', message: `Effluent COD ${dafResult.cod.toFixed(0)} mg/L exceeds limit of ${stdLimits.cod} mg/L`, severity: 'critical', time: fmt(now) });
    if (dafResult.tss > stdLimits.tss)
      list.push({ id: 'tss', parameter: 'TSS', message: `DAF effluent TSS ${dafResult.tss.toFixed(0)} mg/L exceeds limit of ${stdLimits.tss} mg/L`, severity: 'critical', time: fmt(now) });
    else if (dafResult.tss > stdLimits.tss * 0.8)
      list.push({ id: 'tss', parameter: 'TSS', message: `TSS ${dafResult.tss.toFixed(0)} mg/L — within 20% of limit`, severity: 'warning', time: fmt(now) });
    if (dafResult.oilGrease > stdLimits.oilGrease)
      list.push({ id: 'og', parameter: 'O&G', message: `Oil & Grease ${dafResult.oilGrease.toFixed(1)} mg/L exceeds limit of ${stdLimits.oilGrease} mg/L`, severity: 'critical', time: fmt(now) });
    if (dafResult.ph < stdLimits.phMin || dafResult.ph > stdLimits.phMax)
      list.push({ id: 'ph', parameter: 'pH', message: `Post-coagulant pH ${dafResult.ph.toFixed(1)} outside ${stdLimits.phMin}–${stdLimits.phMax} range`, severity: 'warning', time: fmt(now) });
    if (dafResult.ammoniaN > stdLimits.ammoniaN)
      list.push({ id: 'nh3', parameter: 'Ammonia', message: `NH₃-N ${dafResult.ammoniaN.toFixed(1)} mg/L exceeds limit of ${stdLimits.ammoniaN} mg/L`, severity: 'critical', time: fmt(now) });
    if (!list.length) {
      list.push({ id: 'ok1', parameter: 'TSS', message: `DAF effluent TSS ${dafResult.tss.toFixed(0)} mg/L — within spec`, severity: 'info', time: fmt(now) });
      list.push({ id: 'ok2', parameter: 'pH', message: `Post-coagulant pH ${dafResult.ph.toFixed(1)} — stable`, severity: 'info', time: fmt(now) });
      list.push({ id: 'ok3', parameter: 'COD', message: `DAF COD removal ${dafResult.removalPct.cod}% — nominal`, severity: 'info', time: fmt(now) });
      list.push({ id: 'ok4', parameter: 'O&G', message: `Effluent O&G ${dafResult.oilGrease.toFixed(1)} mg/L — compliant`, severity: 'info', time: fmt(now) });
    }
    return list;
  }, [dafResult, stdLimits]);

  /* ─── Animated KPIs — stable ±3% fluctuation for realistic DAF ─── */
  const animatedBOD = useStableKpi(dafResult.bod5, 'BOD₅ effluent', 'mg/L');
  const animatedCOD = useStableKpi(dafResult.cod, 'COD effluent', 'mg/L');
  const animatedTSS = useStableKpi(dafResult.tss, 'TSS effluent', 'mg/L');
  const animatedOG = useStableKpi(dafResult.oilGrease, 'O&G effluent', 'mg/L');
  const animatedFlow = useStableKpi(dafResult.flowRate, 'Flow Rate', 'm³/d');
  const animatedPH = useStableKpi(dafResult.ph, 'pH effluent', '');
  const animatedTemp = useStableKpi(dafResult.temperature, 'Temp', '°C');
  const animatedNH3 = useStableKpi(dafResult.ammoniaN, 'Ammonia', 'mg/L');

  const sparklineBOD = useFlowingSparkline(dafResult.bod5);
  const sparklineCOD = useFlowingSparkline(dafResult.cod);
  const sparklineTSS = useFlowingSparkline(dafResult.tss);
  const sparklineOG = useFlowingSparkline(dafResult.oilGrease);
  const sparklineFlow = useFlowingSparkline(dafResult.flowRate);
  const sparklinePH = useFlowingSparkline(dafResult.ph);
  const sparklineTemp = useFlowingSparkline(dafResult.temperature);
  const sparklineNH3 = useFlowingSparkline(dafResult.ammoniaN);

  // Equipment list for switcher (DAF active, others inactive)
  const EQUIPMENT_LIST = [
    { id: 'daf', name: 'DAF', full: 'Dissolved Air Flotation', icon: Droplets, active: true, removal: `TSS ${dafResult.removalPct.tss}% · COD ${dafResult.removalPct.cod}% · O&G ${dafResult.removalPct.oilGrease}%` },
    { id: 'lamella', name: 'Lamella', full: 'Lamella Clarifier', icon: Layers, active: false, removal: 'TSS 85% · Settleable solids' },
    { id: 'multimedia', name: 'Media Filter', full: 'Multimedia Filter', icon: Filter, active: false, removal: 'TSS 95% · Turbidity' },
    { id: 'gac', name: 'GAC', full: 'Granular Activated Carbon', icon: Beaker, active: false, removal: 'COD 30% · Organics · Colour' },
    { id: 'biological', name: 'Biological', full: 'Activated Sludge / MBBR', icon: FlaskConical, active: false, removal: 'BOD 95% · COD 85% · N&P' },
    { id: 'aeration', name: 'Aeration', full: 'Aeration Basin', icon: Wind, active: false, removal: 'BOD 90% · COD 70%' },
  ];

  // Flowing real-time chart data — DAF effluent
  const [chartData, setChartData] = useState(() => {
    const baseBOD = dafResult.bod5; const baseCOD = dafResult.cod; const baseTSS = dafResult.tss;
    const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    return days.map((d, i) => ({
      date: d,
      bod: baseBOD * (0.96 + i * 0.01),
      cod: baseCOD * (0.96 + i * 0.01),
      tss: baseTSS * (0.96 + i * 0.01),
    }));
  });

  useEffect(() => {
    const interval = setInterval(() => {
      setChartData(prev => {
        const nextBOD = animatedBOD.value || dafResult.bod5;
        const nextCOD = animatedCOD.value || dafResult.cod;
        const nextTSS = animatedTSS.value || dafResult.tss;
        const nextDay = new Date().toLocaleDateString('en-GB', { weekday: 'short' });
        const next = [...prev.slice(1), { date: nextDay, bod: nextBOD, cod: nextCOD, tss: nextTSS }];
        return next;
      });
    }, 5000);
    return () => clearInterval(interval);
  }, [animatedBOD.value, animatedCOD.value, animatedTSS.value, dafResult.bod5, dafResult.cod, dafResult.tss]);

  // ─── KPI status helpers for effluent vs standard ───
  const effluentStatus = (value: number, limit: number) => value > limit ? 'critical' : value > limit * 0.8 ? 'moderate' : 'good';
  const phStatus = (ph: number) => ph >= stdLimits.phMin && ph <= stdLimits.phMax ? 'good' : (ph >= stdLimits.phMin - 0.5 && ph <= stdLimits.phMax + 0.5) ? 'moderate' : 'critical';

  return (
    <div className="space-y-6">
      <PageHeader
        icon={LayoutDashboard}
        title="Dashboard"
        subtitle="System overview — KPIs, active customer context, and quick access"
      />

      {/* ═════ STATS ROW + CONTEXT ═════ */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {stats.map((s, i) => (
          <motion.div key={s.label} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
            className={`rb-card p-4 border ${s.border} ${s.bg} hover:shadow-lg transition-all cursor-pointer`}
            onClick={() => navigate(s.label === 'Customers' ? '/customers' : s.label === 'Samples' ? '/samples' : s.label === 'Reports' ? '/reports' : '/dosing')}>
            <div className="flex items-center justify-between mb-2">
              <s.icon className="w-5 h-5" style={{ color: s.color }} />
              <ChevronRight className="w-4 h-4 text-slate-600" />
            </div>
            <p className="text-2xl font-bold text-white">{s.value}</p>
            <p className="text-xs text-slate-400">{s.label}</p>
          </motion.div>
        ))}
      </div>

      {/* ═════ SYSTEM STATUS + CONTEXT RIBBON ═════ */}
      <div className="rounded-xl border border-slate-800/60 bg-slate-800/40 overflow-hidden">
        <div className="flex items-center gap-2 px-5 py-3 border-b border-slate-800/40 bg-slate-900/40">
          <Activity className="w-3.5 h-3.5 text-slate-400" />
          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">System Status</span>
        </div>
        <div className="px-5 py-3 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-800/50 border border-slate-700">
            <Server className="w-3.5 h-3.5 text-slate-400" />
            <span className="text-[11px] text-slate-300">Data stored locally</span>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border" style={{ background: standard.code === 'UK_EA' ? 'rgba(30,58,95,0.3)' : 'rgba(5,150,105,0.2)', borderColor: standard.code === 'UK_EA' ? 'var(--rb-active)' : '#059669' }}>
            <Globe className="w-3.5 h-3.5 text-emerald-400" />
            <span className="text-[11px] font-medium">{standard.flag} {standard.shortName}</span>
          </div>
          {currentCustomer && (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-cyan-500/5 border border-cyan-500/20">
              <Users className="w-3.5 h-3.5 text-cyan-400" />
              <span className="text-[11px] text-cyan-300">{currentCustomer.company || currentCustomer.name}</span>
            </div>
          )}
          {userProfile?.companyName && (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-rb-brand/5 border border-rb-brand/20">
              <Building2 className="w-3.5 h-3.5 text-rb-brand" />
              <span className="text-[11px] text-rb-brand">{userProfile.companyName}</span>
            </div>
          )}
          {isAdmin ? (
            <button onClick={() => navigate('/backend-setup')}
              className="ml-auto text-[11px] text-amber-400 hover:text-amber-300 transition-colors flex items-center gap-1 px-2 py-1 rounded-md bg-amber-500/5 border border-amber-500/20 hover:bg-amber-500/10 cursor-pointer">
              <Settings className="w-3.5 h-3.5" /> Setup PHP + MySQL Backend
            </button>
          ) : (
            <div
              className="ml-auto text-[11px] text-sky-400 flex items-center gap-1 px-2 py-1 rounded-md bg-sky-500/5 border border-sky-500/20 select-none opacity-80">
              <Database className="w-3.5 h-3.5 text-emerald-400" /> Reynolds Bauhm DB
            </div>
          )}
        </div>
      </div>

      {/* ═════ ONBOARDING — shown only when no data at all ═════ */}
      {samples.length === 0 && customers.length === 0 && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          className="rb-card p-6 border border-rb-brand/30 bg-gradient-to-br from-rb-brand/5 to-transparent">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-rb-brand/15 border border-rb-brand/30 flex items-center justify-center">
              <Zap className="w-5 h-5 text-rb-brand" />
            </div>
            <div>
              <h2 className="text-base font-bold text-slate-100">Welcome to Reynolds &amp; Bauhm Smart Water</h2>
              <p className="text-xs text-slate-500">Get started in 3 steps to unlock full assessment capabilities</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[
              { step: '1', label: 'Add a Customer', desc: 'Register your first site or client', icon: Users, route: '/customers', color: 'text-cyan-400', bg: 'bg-cyan-500/10', border: 'border-cyan-500/20' },
              { step: '2', label: 'Enter Influent Data', desc: 'Log raw wastewater parameters', icon: Droplets, route: '/influent-entry', color: 'text-sky-400', bg: 'bg-sky-500/10', border: 'border-sky-500/20' },
              { step: '3', label: 'Run Assessment', desc: 'Calculate treatability & DAF spec', icon: Play, route: '/parameters', color: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20' },
            ].map(item => (
              <button key={item.step} onClick={() => navigate(item.route)}
                className={`flex items-center gap-3 p-4 rounded-xl ${item.bg} border ${item.border} hover:opacity-90 transition-all text-left`}>
                <div className={`w-8 h-8 rounded-lg ${item.bg} border ${item.border} flex items-center justify-center shrink-0`}>
                  <item.icon className={`w-4 h-4 ${item.color}`} />
                </div>
                <div>
                  <p className="text-xs font-bold text-slate-100">{item.step}. {item.label}</p>
                  <p className="text-[10px] text-slate-500">{item.desc}</p>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-600 ml-auto" />
              </button>
            ))}
          </div>
        </motion.div>
      )}

      {/* ═════ DEMO DATA BANNER ═════ */}
      {!cp.bod5 && !cp.cod && !cp.tss && (
        <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
          className="flex items-start gap-3 px-4 py-3 rounded-xl bg-amber-500/8 border border-amber-500/25 text-amber-300">
          <Info className="w-4 h-4 mt-0.5 shrink-0 text-amber-400" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-amber-300">Showing demo data — no influent parameters entered yet</p>
            <p className="text-[11px] text-amber-400/70 mt-0.5">
              DAF effluent KPIs are calculated from typical defaults (BOD 850 mg/L · COD 1350 mg/L · TSS 420 mg/L).
              Enter real influent data to see site-specific results.
            </p>
          </div>
          <button onClick={() => navigate('/influent-entry')}
            className="shrink-0 text-[11px] font-semibold text-amber-300 hover:text-amber-200 bg-amber-500/15 hover:bg-amber-500/25 border border-amber-500/30 px-3 py-1.5 rounded-lg transition-all whitespace-nowrap">
            Enter Influent Data →
          </button>
        </motion.div>
      )}

      {/* ═════ DAF EFFLUENT KPI SECTION — Yellow Frame + Equipment Switcher ═════ */}
      <div className="rounded-2xl border-2 border-rb-brand/40 bg-gradient-to-br from-rb-brand/5 via-slate-900/50 to-rb-brand/5 p-1">
        {/* Header Banner */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-rb-brand/20">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-rb-brand/15 border border-rb-brand/30">
              <Droplets className="w-4 h-4 text-rb-brand" />
              <span className="text-sm font-bold text-rb-brand tracking-wide">DAF Effluent Data</span>
            </div>
            <span className="text-[11px] text-slate-500 hidden sm:inline">Physics-based model — coagulant, polymer, A/S ratio, hydraulic loading</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-slate-500">Standard:</span>
            <span className="text-[11px] font-medium text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2 py-0.5 rounded-md">{standard.shortName}</span>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-1 p-1">
          {/* LEFT: Equipment Switcher Sidebar */}
          <div className="lg:w-56 shrink-0">
            <div className="rounded-xl bg-rb-surface border border-rb-brand/15 p-3 h-full">
              <h4 className="text-[10px] font-bold text-rb-brand uppercase tracking-wider mb-3 flex items-center gap-1.5">
                <Factory className="w-3 h-3" /> Equipment Selector
              </h4>
              <div className="space-y-1.5">
                {EQUIPMENT_LIST.map((eq) => {
                  const EqIcon = eq.icon;
                  return (
                    <button
                      key={eq.id}
                      disabled={!eq.active}
                      className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-left transition-all ${
                        eq.active
                          ? 'bg-rb-brand/15 border border-rb-brand/40 text-white cursor-default'
                          : 'bg-slate-800/40 border border-slate-800/40 text-slate-500 hover:bg-slate-800/60 cursor-not-allowed opacity-60'
                      }`}
                    >
                      <div className={`w-7 h-7 rounded-md flex items-center justify-center shrink-0 ${eq.active ? 'bg-rb-brand/20' : 'bg-slate-800'}`}>
                        <EqIcon className={`w-3.5 h-3.5 ${eq.active ? 'text-rb-brand' : 'text-slate-500'}`} />
                      </div>
                      <div className="min-w-0">
                        <p className={`text-xs font-semibold ${eq.active ? 'text-white' : 'text-slate-400'}`}>{eq.name}</p>
                        <p className="text-[10px] text-slate-500 truncate">{eq.removal}</p>
                      </div>
                      {eq.active && <div className="ml-auto w-1.5 h-1.5 rounded-full bg-rb-brand animate-pulse" />}
                    </button>
                  );
                })}
              </div>
              <div className="mt-3 p-2 rounded-lg bg-slate-800/40 border border-slate-800/30">
                <p className="text-[10px] text-slate-500 leading-relaxed">
                  <Info className="w-3 h-3 inline mr-1 text-rb-brand" />
                  Physics-based DAF model: coagulant chemistry, polymer bridging, A/S ratio, hydraulic loading, and flocculation quality all contribute to effluent values. Fluctuation ±3% reflects stable operation.
                </p>
              </div>
            </div>
          </div>

          {/* RIGHT: KPI Grid */}
          <div className="flex-1">
            <motion.div variants={staggerContainer} initial="hidden" animate="visible"
              className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
              <KpiCard
                title="BOD₅" value={animatedBOD.value} unit="mg/L"
                status={effluentStatus(animatedBOD.value, stdLimits.bod5)}
                alert={animatedBOD.isAlert}
                sparklineData={sparklineBOD}
                trend={animatedBOD.direction === 'up' ? 'up' : animatedBOD.direction === 'down' ? 'down' : 'flat'}
                trendValue={Number(animatedBOD.pctChange.toFixed(1))}
                limit={stdLimits.bod5} limitLabel="Std"
                delay={0} onClick={() => navigate('/parameters')} />
              <KpiCard
                title="COD" value={animatedCOD.value} unit="mg/L"
                status={effluentStatus(animatedCOD.value, stdLimits.cod)}
                alert={animatedCOD.isAlert}
                sparklineData={sparklineCOD}
                trend={animatedCOD.direction === 'up' ? 'up' : animatedCOD.direction === 'down' ? 'down' : 'flat'}
                trendValue={Number(animatedCOD.pctChange.toFixed(1))}
                limit={stdLimits.cod} limitLabel="Std"
                delay={0.05} onClick={() => navigate('/parameters')} />
              <KpiCard
                title="TSS" value={animatedTSS.value} unit="mg/L"
                status={effluentStatus(animatedTSS.value, stdLimits.tss)}
                alert={animatedTSS.isAlert}
                sparklineData={sparklineTSS}
                trend={animatedTSS.direction === 'up' ? 'up' : animatedTSS.direction === 'down' ? 'down' : 'flat'}
                trendValue={Number(animatedTSS.pctChange.toFixed(1))}
                limit={stdLimits.tss} limitLabel="Std"
                delay={0.1} onClick={() => navigate('/parameters')} />
              <KpiCard
                title="O&G" value={animatedOG.value} unit="mg/L"
                status={effluentStatus(animatedOG.value, stdLimits.oilGrease)}
                alert={animatedOG.isAlert}
                sparklineData={sparklineOG}
                trend={animatedOG.direction === 'up' ? 'up' : animatedOG.direction === 'down' ? 'down' : 'flat'}
                trendValue={Number(animatedOG.pctChange.toFixed(1))}
                limit={stdLimits.oilGrease} limitLabel="Std"
                delay={0.15} onClick={() => navigate('/parameters')} />
              <KpiCard
                title="Flow Rate" value={animatedFlow.value} unit="m³/d"
                status="neutral"
                alert={animatedFlow.isAlert}
                sparklineData={sparklineFlow}
                trend={animatedFlow.direction === 'up' ? 'up' : animatedFlow.direction === 'down' ? 'down' : 'flat'}
                trendValue={Number(animatedFlow.pctChange.toFixed(1))}
                delay={0.2} />
              <KpiCard
                title="pH" value={animatedPH.value} unit=""
                status={phStatus(animatedPH.value)}
                alert={animatedPH.isAlert}
                sparklineData={sparklinePH}
                trend={animatedPH.direction === 'up' ? 'up' : animatedPH.direction === 'down' ? 'down' : 'flat'}
                trendValue={Number(animatedPH.pctChange.toFixed(1))}
                limit={stdLimits.phMax} limitLabel="Max"
                delay={0.25} />
              <KpiCard
                title="Ammonia" value={animatedNH3.value} unit="mg/L"
                status={effluentStatus(animatedNH3.value, stdLimits.ammoniaN)}
                alert={animatedNH3.isAlert}
                sparklineData={sparklineNH3}
                trend={animatedNH3.direction === 'up' ? 'up' : animatedNH3.direction === 'down' ? 'down' : 'flat'}
                trendValue={Number(animatedNH3.pctChange.toFixed(1))}
                limit={stdLimits.ammoniaN} limitLabel="Std"
                delay={0.3} />
              <KpiCard
                title="Temp" value={animatedTemp.value} unit="°C"
                status={animatedTemp.value > stdLimits.temperature ? 'critical' : animatedTemp.value < 10 ? 'critical' : (animatedTemp.value > stdLimits.temperature * 0.85 || animatedTemp.value < 15) ? 'moderate' : 'good'}
                alert={animatedTemp.isAlert}
                sparklineData={sparklineTemp}
                trend={animatedTemp.direction === 'up' ? 'up' : animatedTemp.direction === 'down' ? 'down' : 'flat'}
                trendValue={Number(animatedTemp.pctChange.toFixed(1))}
                limit={stdLimits.temperature} limitLabel="Max"
                delay={0.35} />
            </motion.div>
          </div>
        </div>

        {/* DAF Operating Parameters + Dosing Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-1 p-1">
          {/* Dosing Cards */}
          <div className="lg:col-span-1 rounded-xl bg-rb-surface border border-rb-brand/15 p-3">
            <h4 className="text-[10px] font-bold text-rb-brand uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <FlaskConical className="w-3 h-3" /> Active Dosing
            </h4>
            <div className="space-y-2">
              {/* Coagulant Card */}
              <div className="p-2.5 rounded-lg bg-slate-800/50 border border-slate-800/40">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded bg-amber-500/15 flex items-center justify-center">
                      <Beaker className="w-3 h-3 text-amber-400" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-white">{dafOp.coagulantType === 'fecl3' ? 'FeCl₃' : dafOp.coagulantType === 'alum' ? 'Alum' : dafOp.coagulantType === 'pac' ? 'PAC' : 'None'}</p>
                      <p className="text-[10px] text-slate-500">Coagulant</p>
                    </div>
                  </div>
                  <span className="text-sm font-bold text-amber-400">{dafOp.coagulantDose.toFixed(1)} <span className="text-[10px] text-slate-500 font-normal">mg/L</span></span>
                </div>
                <div className="w-full h-1 bg-slate-700 rounded-full overflow-hidden mt-1.5">
                  <div
                    className="h-full bg-amber-500 rounded-full animate-pulse"
                    style={{ width: `${Math.min(100, (dafOp.coagulantDose / 60) * 100)}%` }}
                  />
                </div>
                <p className="text-[10px] text-slate-500 mt-1">Charge neutralisation + sweep flocculation</p>
              </div>
              {/* Polymer Card */}
              <div className="p-2.5 rounded-lg bg-slate-800/50 border border-slate-800/40">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded bg-purple-500/15 flex items-center justify-center">
                      <Droplet className="w-3 h-3 text-purple-400" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-white">Polymer</p>
                      <p className="text-[10px] text-slate-500">Flocculant</p>
                    </div>
                  </div>
                  <span className="text-sm font-bold text-purple-400">{dafOp.polymerDose.toFixed(1)} <span className="text-[10px] text-slate-500 font-normal">mg/L</span></span>
                </div>
                <div className="w-full h-1 bg-slate-700 rounded-full overflow-hidden mt-1.5">
                  <div
                    className="h-full bg-purple-500 rounded-full animate-pulse"
                    style={{ width: `${Math.min(100, (dafOp.polymerDose / 5) * 100)}%` }}
                  />
                </div>
                <p className="text-[10px] text-slate-500 mt-1">Bridge formation — critical for bubble attachment</p>
              </div>
            </div>
          </div>

          {/* DAF Physics / Operating Parameters */}
          <div className="lg:col-span-2 rounded-xl bg-rb-surface border border-rb-brand/15 p-3">
            <h4 className="text-[10px] font-bold text-rb-brand uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <Settings className="w-3 h-3" /> DAF Operating Parameters
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <div className="p-2 rounded-lg bg-slate-800/40 border border-slate-800/30">
                <p className="text-[10px] text-slate-500 mb-0.5">Recycle Rate</p>
                <p className="text-sm font-bold text-cyan-400">{dafOp.recycleRate}%</p>
                <p className="text-[10px] text-slate-600">Air injection volume</p>
              </div>
              <div className="p-2 rounded-lg bg-slate-800/40 border border-slate-800/30">
                <p className="text-[10px] text-slate-500 mb-0.5">Saturator Pressure</p>
                <p className="text-sm font-bold text-cyan-400">{dafOp.saturatorPressure.toFixed(1)} <span className="text-[10px]">bar</span></p>
                <p className="text-[10px] text-slate-600">Air solubility driver</p>
              </div>
              <div className="p-2 rounded-lg bg-slate-800/40 border border-slate-800/30">
                <p className="text-[10px] text-slate-500 mb-0.5">Hydraulic Loading</p>
                <p className="text-sm font-bold text-cyan-400">{dafOp.hydraulicLoading.toFixed(1)} <span className="text-[10px]">m/hr</span></p>
                <p className="text-[10px] text-slate-600">Surface overflow rate</p>
              </div>
              <div className="p-2 rounded-lg bg-slate-800/40 border border-slate-800/30">
                <p className="text-[10px] text-slate-500 mb-0.5">DAF Retention</p>
                <p className="text-sm font-bold text-cyan-400">{dafOp.retentionTime.toFixed(0)} <span className="text-[10px]">min</span></p>
                <p className="text-[10px] text-slate-600">Cell residence time</p>
              </div>
              <div className="p-2 rounded-lg bg-slate-800/40 border border-slate-800/30">
                <p className="text-[10px] text-slate-500 mb-0.5">Floc G-Value</p>
                <p className="text-sm font-bold text-emerald-400">{dafOp.flocGValue.toFixed(0)} <span className="text-[10px]">s⁻¹</span></p>
                <p className="text-[10px] text-slate-600">Mixing intensity</p>
              </div>
              <div className="p-2 rounded-lg bg-slate-800/40 border border-slate-800/30">
                <p className="text-[10px] text-slate-500 mb-0.5">Floc Retention</p>
                <p className="text-sm font-bold text-emerald-400">{dafOp.flocRetention.toFixed(0)} <span className="text-[10px]">min</span></p>
                <p className="text-[10px] text-slate-600">Flocculation time</p>
              </div>
              <div className="p-2 rounded-lg bg-slate-800/40 border border-slate-800/30">
                <p className="text-[10px] text-slate-500 mb-0.5">A/S Ratio</p>
                <p className="text-sm font-bold text-rb-brand">{dafResult.airToSolids}</p>
                <p className="text-[10px] text-slate-600">Air-to-solids ratio</p>
              </div>
              <div className="p-2 rounded-lg bg-slate-800/40 border border-slate-800/30">
                <p className="text-[10px] text-slate-500 mb-0.5">Floc Quality</p>
                <p className="text-sm font-bold text-rb-brand">{dafResult.flocQuality}</p>
                <p className="text-[10px] text-slate-600">G-value × retention</p>
              </div>
            </div>
            <div className="mt-2 p-2 rounded-lg bg-slate-800/30 border border-slate-700/20">
              <p className="text-[10px] text-slate-500 leading-relaxed">
                <Info className="w-3 h-3 inline mr-1 text-rb-brand" />
                Effluent calculated from physics model: coagulant chemistry ({dafOp.coagulantType === 'fecl3' ? 'FeCl₃ acidic, -0.025 pH per 25 mg/L' : dafOp.coagulantType === 'alum' ? 'Alum mildly acidic' : 'PAC pre-hydrolysed'}),
                polymer bridging, A/S ratio at {dafResult.airToSolids} (optimal 0.03–0.08),
                hydraulic loading {dafOp.hydraulicLoading < 5 ? 'low — oversized' : dafOp.hydraulicLoading > 12 ? 'high — risk of carry-over' : 'optimal'}.
                Realistic ±3% fluctuation for stable DAF operation.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ═════ SAVED ASSESSMENTS & DOSING ═════ */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}>
        <SavedAssessmentsDosing />
      </motion.div>

      {/* ═════ MAIN CONTENT GRID ═════ */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* LEFT: Chart + Process Overview */}
        <div className="lg:col-span-2 space-y-6">
          {/* Parameter Trends Chart */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="rb-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wider flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-rb-brand" /> DAF Effluent Trends
              </h3>
              <button onClick={() => navigate('/trends')} className="text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-1">
                View All <ArrowRight className="w-3 h-3" />
              </button>
            </div>
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="gradBod" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#06b6d4" stopOpacity={0.3}/><stop offset="95%" stopColor="#06b6d4" stopOpacity={0}/></linearGradient>
                  <linearGradient id="gradCod" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3}/><stop offset="95%" stopColor="#8b5cf6" stopOpacity={0}/></linearGradient>
                  <linearGradient id="gradTss" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3}/><stop offset="95%" stopColor="#f59e0b" stopOpacity={0}/></linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis dataKey="date" stroke="#64748b" tick={{ fill: 'var(--rb-text-3)', fontSize: 12 }} />
                <YAxis stroke="#64748b" tick={{ fill: 'var(--rb-text-3)', fontSize: 12 }} />
                <Tooltip contentStyle={{ backgroundColor: '#1e293b', borderColor: '#334155', borderRadius: '0.5rem', color: 'var(--rb-text)' }} itemStyle={{ color: 'var(--rb-text)' }} />
                <Legend wrapperStyle={{ color: 'var(--rb-text-2)', fontSize: '12px' }} />
                <Area type="monotone" dataKey="bod" name="BOD" stroke="#06b6d4" strokeWidth={2} fill="url(#gradBod)" />
                <Area type="monotone" dataKey="cod" name="COD" stroke="#8b5cf6" strokeWidth={2} fill="url(#gradCod)" />
                <Area type="monotone" dataKey="tss" name="TSS" stroke="#f59e0b" strokeWidth={2} fill="url(#gradTss)" />
              </AreaChart>
            </ResponsiveContainer>
          </motion.div>

          {/* Treatment Process Overview */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }} className="rb-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wider flex items-center gap-2">
                <Factory className="w-4 h-4 text-rb-brand" /> Treatment Process Stages
              </h3>
              <span className="text-[10px] text-slate-500">Typical wastewater treatment train</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {PROCESS_STAGES.map((stage, i) => (
                <motion.div key={stage.name} initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 + i * 0.05 }}
                  className="p-3 rounded-xl bg-slate-800/30 border border-slate-800/50 hover:border-slate-600 transition-all group cursor-default">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${stage.color}15` }}>
                      <stage.icon className="w-4 h-4" style={{ color: stage.color }} />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-white">{stage.name}</p>
                      <p className="text-[10px] text-slate-500">{stage.purpose}</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {stage.steps.map(s => (
                      <span key={s} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-400 border border-slate-700">{s}</span>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Parameter Guide (toggleable) */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="rb-card p-6">
            <button onClick={() => setShowGuides(!showGuides)}
              className="w-full flex items-center justify-between text-left group">
              <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wider flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-rb-brand" /> Water Quality Parameter Guide
              </h3>
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-slate-500">{showGuides ? 'Click to collapse' : 'Click to expand'}</span>
                <ChevronRight className={`w-4 h-4 text-slate-500 transition-transform ${showGuides ? 'rotate-90' : ''}`} />
              </div>
            </button>
            {showGuides && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="mt-4 space-y-3">
                {PARAM_GUIDES.map((pg) => (
                  <div key={pg.key} className="p-3 rounded-xl bg-slate-800/30 border border-slate-800/50 hover:border-slate-600 transition-all">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `${pg.color}15` }}>
                        <pg.icon className="w-3.5 h-3.5" style={{ color: pg.color }} />
                      </div>
                      <div>
                        <span className="text-xs font-bold text-white">{pg.name}</span>
                        <span className="text-[10px] text-slate-500 ml-2">{pg.full}</span>
                      </div>
                    </div>
                    <p className="text-[11px] text-slate-400 leading-relaxed mb-2">{pg.desc}</p>
                    <div className="flex items-center gap-2">
                      {pg.ranges.map((r, i) => (
                        <span key={i} className={`text-[10px] px-1.5 py-0.5 rounded bg-slate-800 border border-slate-700 ${r.color}`}>
                          {r.label}{r.max ? ` <${r.max}` : ''}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </motion.div>
            )}
          </motion.div>
        </div>

        {/* RIGHT: Quick Actions + Alerts + Gauges + Activity */}
        <div className="space-y-4">
          {/* Quick Actions */}
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.4 }} className="rb-card p-4">
            <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
              <Target className="w-4 h-4 text-rb-brand" /> Quick Actions
            </h3>
            <div className="space-y-2">
              {quickActions.map(action => (
                <FeatureCard
                  key={action.label}
                  icon={action.icon}
                  title={action.label}
                  desc={action.desc}
                  color={action.color}
                  onClick={action.onClick}
                  variant="compact"
                />
              ))}
            </div>
          </motion.div>

          {/* Pending Tasks */}
          {(() => {
            const tasks: Array<{ label: string; route: string; severity: 'critical' | 'warning' | 'info' }> = [];
            const lastSample = recentSamples[0];
            if (lastSample) {
              const d = daysSince(lastSample.date);
              if (d > 14) tasks.push({ label: `No new sample in ${d} days — overdue`, route: '/samples', severity: 'critical' });
              else if (d > 7) tasks.push({ label: `Last sample was ${d} days ago`, route: '/samples', severity: 'warning' });
            } else {
              tasks.push({ label: 'No samples logged — add first sample', route: '/samples', severity: 'warning' });
            }
            if (customers.length === 0) tasks.push({ label: 'No customers on file — add one', route: '/customers', severity: 'info' });
            if (activeDosingCount === 0 && dosingConfigs.length === 0) tasks.push({ label: 'No dosing configured', route: '/dosing', severity: 'info' });
            const nonCompliant = recentSamples.filter(s => {
              const c = computeSampleCompliance(s, stdLimits as any);
              return c >= 0 && c < 80;
            });
            if (nonCompliant.length > 0)
              tasks.push({ label: `${nonCompliant.length} sample(s) below 80% compliance`, route: '/samples', severity: 'critical' });
            if (!tasks.length) return null;
            return (
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.42 }} className="rb-card p-4">
                <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-cyan-400" /> Pending Tasks
                  <span className="ml-auto text-xs bg-red-500/10 text-red-400 border border-red-500/20 px-1.5 py-0.5 rounded-full">{tasks.filter(t => t.severity === 'critical').length || tasks.length}</span>
                </h3>
                <div className="space-y-1.5">
                  {tasks.map((t, i) => (
                    <button key={i} onClick={() => navigate(t.route)}
                      className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-left transition-all hover:bg-slate-800/60 border ${
                        t.severity === 'critical' ? 'border-red-500/20 bg-red-500/5' :
                        t.severity === 'warning'  ? 'border-amber-500/20 bg-amber-500/5' :
                                                    'border-slate-800/40 bg-slate-800/20'
                      }`}>
                      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${t.severity === 'critical' ? 'bg-red-400' : t.severity === 'warning' ? 'bg-amber-400' : 'bg-slate-500'}`} />
                      <span className={`text-xs flex-1 ${t.severity === 'critical' ? 'text-red-300' : t.severity === 'warning' ? 'text-amber-300' : 'text-slate-400'}`}>{t.label}</span>
                      <ChevronRight className="w-3 h-3 text-slate-600" />
                    </button>
                  ))}
                </div>
              </motion.div>
            );
          })()}

          {/* Recent Alerts */}
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.45 }} className="rb-card p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wider flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-400" /> Recent Alerts
              </h3>
              <span className="text-xs text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded-full">{alerts.length}</span>
            </div>
            <div className="space-y-2">
              {alerts.map(alert => (
                <div key={alert.id} className="flex items-start gap-3 p-2 rounded-lg hover:bg-slate-800/50 transition-colors">
                  <div className={`w-1 h-8 rounded-full ${alert.severity === 'critical' ? 'bg-red-500' : alert.severity === 'warning' ? 'bg-amber-500' : 'bg-cyan-500'}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-300 font-medium">{alert.parameter}</p>
                    <p className="text-xs text-slate-500">{alert.message}</p>
                  </div>
                  <span className="text-[10px] text-slate-600 flex-shrink-0">{alert.time}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Parameter Gauges — DAF Effluent */}
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.5 }} className="rb-card p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wider">Parameter Gauges</h3>
              <span className="text-[10px] text-rb-brand bg-rb-brand/10 border border-rb-brand/20 px-2 py-0.5 rounded-md">DAF Effluent</span>
            </div>
            <div className="flex flex-wrap justify-center gap-5">
              <Gauge title="Ammonia" value={dafResult.ammoniaN} unit="mg/L" min={0} max={Math.max(10, stdLimits.ammoniaN * 2)} limit={stdLimits.ammoniaN} size="md" delay={0.5} />
              <Gauge title="pH" value={dafResult.ph} unit="" min={0} max={14} limit={stdLimits.phMax} size="md" delay={0.6} />
              <Gauge title="O&G" value={dafResult.oilGrease} unit="mg/L" min={0} max={Math.max(30, stdLimits.oilGrease * 2)} limit={stdLimits.oilGrease} size="md" delay={0.7} />
              <Gauge title="Conductivity" value={dafResult.conductivity} unit="µS" min={0} max={5000} limit={stdLimits.conductivity ?? 2500} size="md" delay={0.8} />
            </div>
          </motion.div>

          {/* Active Dosing Summary */}
          {dosingConfigs.length > 0 && (
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.55 }} className="rb-card p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wider flex items-center gap-2">
                  <FlaskConical className="w-4 h-4 text-purple-400" /> Active Dosing
                </h3>
                <button onClick={() => navigate('/dosing')} className="text-[10px] text-purple-400 hover:underline">Manage</button>
              </div>
              <div className="space-y-1.5">
                {dosingConfigs.slice(0, 5).map((d: { chemicalId?: string; enabled?: boolean; dose?: number }) => {
                  const chem = AVAILABLE_CHEMICALS.find(c => c.id === d.chemicalId);
                  if (!chem) return null;
                  return (
                    <div key={chem.id} className="flex items-center justify-between py-1">
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full shrink-0" style={{ background: d.enabled ? chemColor(chem.id) : 'var(--rb-text-4)' }} />
                        <span className={`text-xs ${d.enabled ? 'text-white' : 'text-slate-500'}`}>{chem.name}</span>
                      </div>
                      <span className={`text-xs font-mono ${d.enabled ? 'text-cyan-400' : 'text-slate-600'}`}>{d.dose} {chem.doseUnit}</span>
                    </div>
                  );
                })}
                {dosingConfigs.length > 5 && (
                  <p className="text-[10px] text-slate-500 text-center pt-1">+{dosingConfigs.length - 5} more chemicals</p>
                )}
              </div>
            </motion.div>
          )}

          {/* Recent Activity */}
          {activities.length > 0 && (
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.6 }} className="rb-card p-4">
              <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                <Activity className="w-4 h-4 text-cyan-400" /> Recent Activity
              </h3>
              <div className="space-y-2 max-h-48 overflow-auto">
                {activities.slice(0, 8).map((act: { action: string; timestamp: string; username: string; details?: string }, i: number) => (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    <div className="w-1.5 h-1.5 rounded-full bg-slate-600 shrink-0" />
                    <span className="text-slate-400 flex-1 min-w-0 truncate">{act.action}{act.details ? ` — ${act.details}` : ''}</span>
                    <span className="text-[10px] text-slate-600 shrink-0">{new Date(act.timestamp).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </div>
      </div>

      {/* ═════ GAUGES + RECENT SAMPLES ═════ */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Assessment Summary (when available) */}
        {hasCalc && calc.result && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }} className="rb-card p-6">
            <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wider mb-4 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-400" /> Latest Assessment
            </h3>
            <div className="space-y-2">
              <div className="flex items-center justify-between p-2 rounded-lg bg-slate-800/30">
                <span className="text-xs text-slate-400">Classification</span>
                <span className="text-xs font-bold text-cyan-400">{calc.result.treatabilityClassification}</span>
              </div>
              <div className="flex items-center justify-between p-2 rounded-lg bg-slate-800/30">
                <span className="text-xs text-slate-400">Treatability Index</span>
                <span className="text-xs font-bold text-white">{calc.result.overallTreatabilityIndex}</span>
              </div>
              <div className="flex items-center justify-between p-2 rounded-lg bg-slate-800/30">
                <span className="text-xs text-slate-400">Biological Viability</span>
                <span className="text-xs font-bold text-emerald-400">
                  {(calc.result.biologicalViability || []).filter((v: { passFail: string }) => v.passFail === 'PASS').length}/{(calc.result.biologicalViability || []).length} passed
                </span>
              </div>
              <div className="flex items-center justify-between p-2 rounded-lg bg-slate-800/30">
                <span className="text-xs text-slate-400">Toxic Parameters</span>
                <span className={`text-xs font-bold ${(calc.result.toxicParametersCount || 0) > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                  {calc.result.toxicParametersCount || 0} found
                </span>
              </div>
              <div className="flex items-center justify-between p-2 rounded-lg bg-slate-800/30">
                <span className="text-xs text-slate-400">Process Stages</span>
                <span className="text-xs font-bold text-rb-brand">{calc.result.simulation?.stages?.length || 0}</span>
              </div>
              <button onClick={() => navigate('/parameters')}
                className="w-full mt-2 py-2 rounded-lg text-xs font-bold bg-gradient-to-r from-rb-brand to-[#b8931f] text-slate-200 hover:from-[#b8931f] hover:to-[#a07d1a] transition-all">
                View Full Assessment
              </button>
            </div>
          </motion.div>
        )}

        {/* Recent Samples */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55 }} className={`${hasCalc ? 'lg:col-span-2' : 'lg:col-span-3'} rb-card p-6`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-slate-400 uppercase tracking-wider flex items-center gap-2">
              <Beaker className="w-4 h-4 text-rb-brand" /> Recent Samples
            </h3>
            <button onClick={() => navigate('/samples')} className="text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-1">
              View All <ArrowRight className="w-3 h-3" />
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-xs text-slate-500 uppercase tracking-wider">
                  <th className="pb-3 pr-4">Customer</th>
                  <th className="pb-3 pr-4">Sample</th>
                  <th className="pb-3 pr-4">Date</th>
                  <th className="pb-3 pr-4">Compliance</th>
                  <th className="pb-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/50">
                {recentSamples.length > 0 ? recentSamples.map(s => {
                  const compliance = computeSampleCompliance(s, stdLimits as any);
                  const ds = daysSince(s.date);
                  const sName = (s.sampleName || '').toUpperCase();
                  const chainType = sName.includes('INF') ? 'INF' : sName.includes('DAF') ? 'DAF' : sName.includes('EFF') ? 'EFF' : null;
                  return (
                    <tr key={s.customerId + s.sampleName} className="hover:bg-slate-800/30 transition-colors">
                      <td className="py-3 pr-4 text-sm text-slate-300">
                        <div>{s.customerName || '—'}</div>
                      </td>
                      <td className="py-3 pr-4">
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm text-slate-300">{s.sampleName}</span>
                          {chainType && (
                            <span className={`text-[9px] font-bold px-1 py-0.5 rounded border ${
                              chainType === 'INF' ? 'text-sky-400 border-sky-500/30 bg-sky-500/10' :
                              chainType === 'DAF' ? 'text-rb-brand border-rb-brand/30 bg-rb-brand/10' :
                              'text-emerald-400 border-emerald-500/30 bg-emerald-500/10'
                            }`}>{chainType}</span>
                          )}
                        </div>
                      </td>
                      <td className="py-3 pr-4">
                        <div className="text-xs text-slate-500">{new Date(s.date).toLocaleDateString('en-GB')}</div>
                        <div className="text-[10px] text-slate-600">{ds === 0 ? 'Today' : ds === 1 ? 'Yesterday' : `${ds}d ago`}</div>
                      </td>
                      <td className="py-3 pr-4">
                        {compliance < 0 ? (
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-slate-700/40 text-slate-500 border border-slate-800/40">
                            No params
                          </span>
                        ) : (
                          <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border ${
                            compliance >= 90 ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' :
                            compliance >= 70 ? 'bg-amber-500/10 text-amber-400 border-amber-500/20' :
                                              'bg-red-500/10 text-red-400 border-red-500/20'
                          }`}>
                            <Activity className="w-3 h-3" /> {compliance}%
                          </span>
                        )}
                      </td>
                      <td className="py-3 text-right">
                        <button onClick={() => navigate('/samples')} className="text-xs text-cyan-400 hover:text-cyan-300">View</button>
                      </td>
                    </tr>
                  );
                }) : (
                  <tr><td colSpan={5} className="py-8 text-center text-sm text-slate-500">
                    No samples yet. <button onClick={() => navigate('/samples')} className="text-cyan-400 hover:underline">Create one</button>.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </motion.div>

      </div>
    </div>
  );
}
